import { ToastProvider, TooltipProvider } from "@crow/ui";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LucideProvider } from "lucide-react";
import { type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { createQueryClient, type QueryClientOptions } from "./query-client";

export interface AppProvidersProps {
  /** `createQueryClient()`. */
  client: QueryClient;
  /** The charter's line icons: Lucide at a 1.75 stroke. */
  lineIcons?: boolean;
  /** `@crow/ui`'s toasts. Default true. */
  toasts?: boolean;
  children: ReactNode;
}

/** What every page of a web app sits in: its queries, `@crow/ui`'s tooltips and toasts. */
export function AppProviders({ client, lineIcons = false, toasts = true, children }: AppProvidersProps) {
  let tree = <TooltipProvider>{toasts ? <ToastProvider>{children}</ToastProvider> : children}</TooltipProvider>;
  if (lineIcons) tree = <LucideProvider strokeWidth={1.75}>{tree}</LucideProvider>;
  return <QueryClientProvider client={client}>{tree}</QueryClientProvider>;
}

export interface RenderAppOptions extends QueryClientOptions, Pick<AppProvidersProps, "lineIcons" | "toasts"> {
  /**
   * The development mock to install first, given only when it is on, so that a production build
   * leaves it out: `import.meta.env.DEV && import.meta.env.VITE_MOCK === "1" ? () => import("./dev/mock") : undefined`.
   */
  mock?: () => Promise<{ installMock(): void }>;
  /** The service worker to register once the page has loaded (`import.meta.env.PROD ? "/sw.js" : undefined`). */
  serviceWorker?: string;
  /**
   * `app` sits in the browser's router (`BrowserRouter`). Default true; false when `app` brings
   * its own, a data router: `<RouterProvider router={createBrowserRouter(routes)} />`.
   */
  browserRouter?: boolean;
}

/**
 * Starts a web app in `#root`: its query client (`createQueryClient`), the providers and the
 * browser's router (unless `browserRouter` is false) around `app`.
 */
export async function renderApp(app: ReactNode, options: RenderAppOptions = {}): Promise<void> {
  const { lineIcons, toasts, mock, serviceWorker, browserRouter = true, ...queryOptions } = options;
  if (mock) (await mock()).installMock();
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <AppProviders client={createQueryClient(queryOptions)} lineIcons={lineIcons} toasts={toasts}>
        {browserRouter ? <BrowserRouter>{app}</BrowserRouter> : app}
      </AppProviders>
    </StrictMode>,
  );
  if (serviceWorker && "serviceWorker" in navigator)
    window.addEventListener("load", () => void navigator.serviceWorker.register(serviceWorker).catch(() => undefined));
}
